/**
 * @package   EGI / Frontend — wishlist
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-373)
 * @date      2026-07-03
 * @purpose   Merge-at-boot della lista guest (design FRONTEND_WISHLIST.md §3):
 *            trigger al boot su qualunque pagina (il login è full-page, nessun
 *            "momento login" JS). Invariante: la chiave localStorage si rimuove
 *            SOLO dopo 2xx — su errore niente retry aggressivo, si ritenta al
 *            prossimo page load. Dipendenze iniettate → testabile senza rete
 *            (tests/js/wishlist-merge).
 */

import type { WishlistItem, WishlistMergedDetail } from './types';

export interface MergePayloadRow {
    id: number;
    added_at: number;
}

export interface MergeTransportResult {
    merged: number;
    skipped: ReadonlyArray<{ id: string; reason: string }>;
    count: number;
}

export interface MergeDeps {
    read: () => WishlistItem[];
    /** Rimuove la chiave guest — invocata SOLO dopo 2xx. */
    clear: () => void;
    merge: (items: ReadonlyArray<MergePayloadRow>) => Promise<MergeTransportResult>;
}

/**
 * Fonde la lista guest nel server. Ritorna il detail per egi:wishlist:merged,
 * oppure null se non c'era nulla da fondere o il merge è fallito (chiave intatta).
 */
export async function runMergeAtBoot(deps: MergeDeps): Promise<WishlistMergedDetail | null> {
    const guestItems = deps.read();
    if (guestItems.length === 0) {
        return null;
    }

    const payload: MergePayloadRow[] = guestItems
        .map((item) => ({ id: Number.parseInt(item.id, 10), added_at: item.addedAt }))
        .filter((row) => Number.isInteger(row.id) && row.id > 0);

    if (payload.length === 0) {
        // Solo id non riconciliabili col backend (non numerici): chiave rimossa
        // per non ritentare all'infinito un payload che il server rifiuterebbe.
        deps.clear();
        return null;
    }

    try {
        const result = await deps.merge(payload);
        deps.clear(); // ← pulizia SOLO dopo conferma 2xx (design §3)
        return {
            merged: result.merged,
            skipped: result.skipped.map((row) => row.id),
            count: result.count,
        };
    } catch {
        // Chiave NON rimossa: la lista guest non si perde mai; l'errore è già
        // stato riportato dal transport (UEM WISHLIST_MERGE_FAILED).
        return null;
    }
}
